import type { AlertSetupDraft } from './alertSetupDraft'
import { describeSchedule } from './alertSchedule'

/**
 * Why the agent is being asked to write the check:
 * - `create`: the card made a new alert; the rule exists, paused, with no script.
 * - `rewrite`: the card changed an existing alert (or it never had a tested check).
 */
export type AlertSetupMode = 'create' | 'rewrite'

const tableLabel = (t: AlertSetupDraft['tables'][number]) => {
  const path = t.folder_path ? `${t.folder_path}/${t.filename}` : t.filename
  return t.display_name ? `${t.display_name} (${path})` : path
}

/** The text with a full stop, unless it already ends a sentence. */
const stop = (s: string) => (/[.!?]$/.test(s) ? s : `${s}.`)

const joinAnd = (items: string[]) =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`

/** "weekly on Monday at 09:00 (Europe/London)" — the sentence describeSchedule writes, mid-sentence. */
const scheduleClause = (d: AlertSetupDraft) => {
  const s = describeSchedule(d.schedule)
  return s.charAt(0).toLowerCase() + s.slice(1)
}

/**
 * The first message of an alert's setup chat: the card's contents in plain
 * prose, then whatever the user typed or dictated. It is the user's message
 * and reads as one; it carries no instructions. What the agent must do (the
 * rule exists, never create_alert, write, test, save, ask before turning it
 * on) is a hidden system-prompt block the server adds on every turn of a
 * session whose metadata names the alert (agentdeps alertSetupBlock), so a
 * replay never shows it.
 */
export function buildAlertFirstMessage(
  d: AlertSetupDraft,
  words: string,
  opts: { mode?: AlertSetupMode; jobLabel?: (job: string) => string } = {},
): string {
  const job = opts.jobLabel ? opts.jobLabel(d.job) : d.job
  const opening = opts.mode === 'rewrite'
    ? `I want to change my alert "${d.name.trim()}" on job ${job}.`
    : `I want an alert called "${d.name.trim()}" on job ${job}.`
  const watches = d.kind === 'query'
    ? `It watches a question about the job's data: "${d.question.trim()}".`
    : d.tables.length === 1
      ? `It watches the saved table ${tableLabel(d.tables[0])}.`
      : `It watches ${d.tables.length} saved tables: ${joinAnd(d.tables.map(tableLabel))}.`
  const sentences = [
    opening,
    watches,
    stop(`Tell me when: ${d.condition.trim()}`),
    ...(d.context.trim() ? [stop(`Context: ${d.context.trim()}`)] : []),
    `Check it ${scheduleClause(d)}.`,
    ...(d.recipients.length ? [`Also tell ${joinAnd(d.recipients)}.`] : []),
  ]
  const card = sentences.join(' ')
  const said = words.trim()
  return said ? `${card}\n\n${said}` : card
}
